import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, Save, ToggleLeft, ToggleRight, Shield, Settings, X } from 'lucide-react';

const PrivateAccessSettings = ({ onClose }) => {
  const [settings, setSettings] = useState({
    password: '',
    is_enabled: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success or error

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/private-access-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings({
          password: data.password || '',
          is_enabled: data.is_enabled !== undefined ? data.is_enabled : true
        });
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching private access settings:', error);
      setMessage('Error loading settings');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/private-access-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: settings.password,
          is_enabled: settings.is_enabled
        })
      });

      if (response.ok) {
        setMessage('Settings updated successfully!');
        setMessageType('success');
        setTimeout(() => {
          setMessage('');
        }, 3000);
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating private access settings:', error);
      setMessage('Error updating settings');
      setMessageType('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    setSettings(prev => ({
      ...prev,
      password: e.target.value
    }));
  };

  const handleToggleEnabled = () => {
    setSettings(prev => ({
      ...prev,
      is_enabled: !prev.is_enabled
    }));
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal Content */}
        <motion.div
          className="relative z-10 w-full max-w-lg mx-4"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", duration: 0.3 }}
        >
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 relative">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block mb-6">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto">
                    <Settings className="w-8 h-8 text-white" />
                  </div>
                  <motion.div
                    className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Shield className="w-3 h-3 text-white" />
                  </motion.div>
                </div>
              </div>

              <h1 className="text-3xl font-black text-white mb-2">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Private Access
                </span>
              </h1>
              
              <p className="text-lg text-gray-300 mb-2">
                Settings Management
              </p>
              
              <p className="text-sm text-gray-400">
                Configure access control for the landing page
              </p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2">
                  <motion.div
                    className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <span className="text-white">Loading settings...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Private Access Mode
                  </label>
                  <div 
                    className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all duration-300 ${
                      settings.is_enabled 
                        ? 'bg-green-500/20 border-green-500/40' 
                        : 'bg-red-500/20 border-red-500/40'
                    }`}
                    onClick={handleToggleEnabled}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${settings.is_enabled ? 'bg-green-400' : 'bg-red-400'}`} />
                      <div>
                        <p className="text-white font-medium">
                          {settings.is_enabled ? 'Enabled' : 'Disabled'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {settings.is_enabled 
                            ? 'Users need password to access the site' 
                            : 'Users access the site directly'
                          }
                        </p>
                      </div>
                    </div>
                    {settings.is_enabled ? (
                      <ToggleRight className="w-8 h-8 text-green-400" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-red-400" />
                    )}
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Access Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={settings.password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300"
                      placeholder="Enter new password"
                      disabled={isSaving}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      disabled={isSaving}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Change the password users need to access the site
                  </p>
                </div>

                {/* Message */}
                {message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-xl p-3 text-sm text-center ${
                      messageType === 'success' 
                        ? 'bg-green-500/20 border border-green-500/30 text-green-200' 
                        : 'bg-red-500/20 border border-red-500/30 text-red-200'
                    }`}
                  >
                    {message}
                  </motion.div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <motion.button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold py-3 px-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                    whileHover={{ scale: isSaving ? 1 : 1.02 }}
                    whileTap={{ scale: isSaving ? 1 : 0.98 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-center justify-center space-x-2">
                      {isSaving ? (
                        <>
                          <motion.div
                            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          <span>Save Settings</span>
                        </>
                      )}
                    </div>
                  </motion.button>

                  <motion.button
                    onClick={onClose}
                    disabled={isSaving}
                    className="px-6 py-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 text-white font-medium hover:bg-white/20 transition-all duration-300 disabled:opacity-50"
                    whileHover={{ scale: isSaving ? 1 : 1.02 }}
                    whileTap={{ scale: isSaving ? 1 : 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PrivateAccessSettings;